import React, { useState } from 'react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { UploadCloud, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BATCH_SIZE = 500; // Définir la taille du lot ici

const StudentExcelImport = ({ db, schoolId, setMessage, setError, onReturnToEleves, userRole }) => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0); // Nouveau: Progression globale en pourcentage

  // Vérification des rôles d'autorisation
  if (!['admin', 'superAdmin', 'gestionnaire'].includes(userRole)) {
    return (
      <div className="p-4 bg-white rounded-lg shadow-md text-red-600 font-semibold">
        Vous n'avez pas l'autorisation d'importer des élèves. Seuls les administrateurs et gestionnaires peuvent accéder à cette fonctionnalité.
      </div>
    );
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setImportSummary(null);
    setMessage(null);
    setError(null);
    setCurrentBatch(0);
    setTotalBatches(0);
    setOverallProgress(0);
  };

  /**
   * Parse une entrée de date variée en un objet Date JavaScript.
   * Gère les Timestamps Firestore, les objets Date natifs, les nombres (dates Excel) et divers formats de chaînes.
   * @param {*} dateInput - La valeur de la date à parser.
   * @returns {Date|null} L'objet Date parsé ou null si le parsing échoue.
   */
  const parseDateString = (dateInput) => {
    if (!dateInput) return null;

    // 1. Gérer les objets Timestamp de Firestore (par exemple, lors de la lecture de la base de données)
    if (dateInput && typeof dateInput.toDate === 'function') {
      const jsDate = dateInput.toDate();
      return isNaN(jsDate.getTime()) ? null : jsDate;
    }

    // 2. Gérer les objets Date JavaScript natifs (potentiellement déjà parsés par XLSX)
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
      
    }

    // 3. Gérer les nombres sérialisés d'Excel (jours depuis une date de référence)
    // Excel utilise généralement le 30 décembre 1899 comme jour 0.
    if (typeof dateInput === 'number' && dateInput > 0) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 1899-12-30T00:00:00.000Z
      excelEpoch.setDate(excelEpoch.getDate() + dateInput);
      return excelEpoch;
    }

    // 4. Gérer les représentations de dates sous forme de chaînes de caractères
    if (typeof dateInput === 'string') {
      // Tenter des formats spécifiques pour plus de robustesse
      const dateFormats = [
        { regex: /^\d{4}-\d{2}-\d{2}$/, parser: (s) => new Date(s) }, // YYYY-MM-DD
        { regex: /^\d{4}\/\d{2}\/\d{2}$/, parser: (s) => { const p = s.split('/'); return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10)); } }, // YYYY/MM/DD
        { regex: /^\d{2}\/\d{2}\/\d{4}$/, parser: (s) => { const p = s.split('/'); return new Date(parseInt(p[2], 10), parseInt(p[1], 10) - 1, parseInt(p[0], 10)); } }, // DD/MM/YYYY
        { regex: /^\d{2}-\d{2}-\d{4}$/, parser: (s) => { const p = s.split('-'); return new Date(parseInt(p[2], 10), parseInt(p[1], 10) - 1, parseInt(p[0], 10)); } }, // DD-MM-YYYY
      ];

      for (const format of dateFormats) {
        if (dateInput.match(format.regex)) {
          const d = format.parser(dateInput);
          // Vérifier si la date est valide et correspond bien à l'année/mois/jour attendus
          // C'est important car new Date('31/02/2023') peut donner le 3 mars
          if (!isNaN(d.getTime())) { // Basic validity check
            // For DD/MM/YYYY or DD-MM-YYYY, check if parsed day/month/year match
            // This is complex, a simple isNaN(d.getTime()) often suffices if format is strict
            return d;
          }
        }
      }

      // Dernier recours : tenter un parsing générique de chaîne
      const genericDate = new Date(dateInput);
      if (!isNaN(genericDate.getTime())) {
        return genericDate;
      }
    }

     console.warn("parseDateString: Impossible de parser la date (entrée reçue):", dateInput); // Modifier ce log
    return null;
  };

  const handleImport = async () => {
    if (!file) {
      setError("Veuillez sélectionner un fichier Excel.");
      return;
    }
    if (!schoolId) {
      setError("ID de l'école introuvable.");
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setError(null);
    setImportSummary(null);
    setCurrentBatch(0);
    setTotalBatches(0);
    setOverallProgress(0);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // cellDates: true essaie de convertir les cellules de date en objets Date JavaScript
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const studentsToProcess = XLSX.utils.sheet_to_json(worksheet);

          if (studentsToProcess.length === 0) {
            setMessage({ type: 'warning', text: "Le fichier Excel ne contient aucune donnée valide." });
            setIsLoading(false);
            return;
          }

          // --- Étape 1: VÉRIFICATION DES DOUBLONS EXISTANTS DANS LA BASE DE DONNÉES ---
          setMessage({ type: 'info', text: 'Chargement des élèves existants pour la vérification des doublons...' });
          const existingStudentKeys = new Set();
          const existingStudentsSnapshot = await getDocs(collection(db, 'schools', schoolId, 'eleves'));
          existingStudentsSnapshot.forEach(doc => {
            const student = doc.data();
            const dobObject = parseDateString(student.dateOfBirth); // Utiliser la fonction de parsing mise à jour
            const dobFormatted = dobObject ? dobObject.toISOString().split('T')[0] : '';
            const key = `${(student.name || '').toLowerCase()}_${dobFormatted}_${(student.gender || '').toLowerCase()}`;
            existingStudentKeys.add(key);
          });
          setMessage(null); // Clear info message

          let totalStudentsAdded = 0;
          let totalStudentsFailed = 0;
          let totalStudentsDuplicates = 0;
          const allFailedStudents = [];
          const allDuplicateStudents = [];

          const processedKeysInCurrentFile = new Set(); // Pour les doublons au sein du fichier Excel actuel

          // Calculer le nombre total de lots et initialiser la progression
          const numBatches = Math.ceil(studentsToProcess.length / BATCH_SIZE);
          setTotalBatches(numBatches);

          // --- Étape 2: TRAITEMENT DES ÉLÈVES PAR LOTS ---
          for (let i = 0; i < studentsToProcess.length; i += BATCH_SIZE) {
            const currentBatchNumber = Math.floor(i / BATCH_SIZE) + 1;
            setCurrentBatch(currentBatchNumber);

            const batch = studentsToProcess.slice(i, i + BATCH_SIZE);
            const processedStudentsInCurrentBatch = i; // Nombre d'élèves traités jusqu'à présent (avant ce lot)
            const overallProgressValue = Math.round((processedStudentsInCurrentBatch / studentsToProcess.length) * 100);
            setOverallProgress(overallProgressValue);

            // Message de progression spécifique au lot
            setMessage({ type: 'info', text: `Traitement du lot ${currentBatchNumber}/${numBatches} (${overallProgressValue}% complété)...` });

            for (const student of batch) {
              const studentName = student["Nom complet"] || ''; 
              const studentGender = student["Sexe"] || '';
              const rawDateOfBirth = student["Date de Naissance"];
              const parsedDateOfBirth = parseDateString(rawDateOfBirth);
              // --- Ajout pour le débogage ---
              if (!studentName || !parsedDateOfBirth || !studentGender) {
                console.error("Échec d'importation pour cet élève (champs obligatoires) :", {
                  excelRow: student,
                  nomComplet: studentName,
                  rawDateOfBirth: rawDateOfBirth,
                  parsedDateOfBirth: parsedDateOfBirth,
                  sexe: studentGender
                });
                totalStudentsFailed++;
                allFailedStudents.push({ data: student, error: "Champs obligatoires (Nom complet, Date de Naissance valide, Sexe) manquants ou invalides." });
                continue;
              }
              // --- Fin de l'ajout ---

              const dobForDuplicateCheck = parsedDateOfBirth ? parsedDateOfBirth.toISOString().split('T')[0] : '';
              const studentKey = `${studentName.toLowerCase()}_${dobForDuplicateCheck}_${studentGender.toLowerCase()}`;

              // Validation minimale des champs obligatoires
              if (!studentName || !parsedDateOfBirth || !studentGender) {
                totalStudentsFailed++;
                allFailedStudents.push({ data: student, error: "Champs obligatoires (Nom complet, Date de Naissance valide, Sexe) manquants ou invalides." });
                continue;
              }

              // Vérification des doublons (existant en base OU dans le fichier actuel)
              if (existingStudentKeys.has(studentKey)) {
                totalStudentsDuplicates++;
                allDuplicateStudents.push({ data: student, reason: "Doublon existant dans la base de données." });
                continue;
              }
              if (processedKeysInCurrentFile.has(studentKey)) {
                totalStudentsDuplicates++;
                allDuplicateStudents.push({ data: student, reason: "Doublon trouvé plusieurs fois dans le fichier Excel." });
                continue;
              }

              // Ajouter la clé pour suivre les doublons dans le fichier actuel
              processedKeysInCurrentFile.add(studentKey);

              const studentData = {
                name: studentName,
                dateOfBirth: parsedDateOfBirth, // parsedDateOfBirth est maintenant un objet Date JavaScript
                placeOfBirth: student["Lieu de Naissance"] || '',
                address: student["Adresse"] || '',
                gender: studentGender,
                guardianFullName: student["Nom Complet du Tuteur"] || '',
                guardianPhone: student["Téléphone du Tuteur"] || '',
                fatherName: student["Nom du Père"] || '',
                motherName: student["Nom de la Mère"] || '',
                photoUrl: student["URL Photo"] || '',
                schoolId: schoolId, // Assurez-vous que le schoolId est ajouté au document
                createdAt: new Date(),
                updatedAt: new Date(),
              };

              try {
                // Firebase Firestore convertira automatiquement les objets Date JavaScript en Timestamps lors de l'ajout
                await addDoc(collection(db, 'schools', schoolId, 'eleves'), studentData);
                totalStudentsAdded++;
                existingStudentKeys.add(studentKey); // Ajouter à l'ensemble des clés existantes pour éviter les doublons dans les lots suivants
              } catch (addError) {
                totalStudentsFailed++;
                allFailedStudents.push({ data: student, error: addError.message });
              }
            }
          } // Fin de la boucle de traitement des lots

          // Mettre à jour la progression finale à 100%
          setOverallProgress(100);

          // --- Étape 3: PRÉSENTATION DU RÉSUMÉ ET DES RÉSULTATS FINALS ---
          setImportSummary({
            studentsAdded: totalStudentsAdded,
            studentsFailed: totalStudentsFailed,
            studentsDuplicates: totalStudentsDuplicates,
            failedStudents: allFailedStudents,
            duplicateStudents: allDuplicateStudents
          });

          if (totalStudentsAdded > 0) {
            setMessage({ type: 'success', text: `${totalStudentsAdded} élèves importés avec succès.` });
          }
          if (totalStudentsFailed > 0) {
            setError(`Attention: ${totalStudentsFailed} erreurs d’importation et ${totalStudentsDuplicates} doublons. Voir les détails.`);
          } else if (totalStudentsDuplicates > 0) {
            setMessage({ type: 'warning', text: `${totalStudentsDuplicates} doublons ignorés.` });
          } else {
            setMessage({ type: 'success', text: 'Importation terminée sans erreur ni doublon.' });
          }

        } catch (parseError) {
          setError("Erreur lors de la lecture ou du traitement du fichier : " + parseError.message);
        } finally {
          setIsLoading(false);
          setCurrentBatch(0);
          setTotalBatches(0);
          // La progression finale reste à 100% ou à la dernière valeur calculée
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (e) {
      setError("Erreur inattendue : " + e.message);
      setIsLoading(false);
      setCurrentBatch(0);
      setTotalBatches(0);
      setOverallProgress(0);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Importer des Élèves via Excel</h2>
      <p className="text-gray-600 mb-4">
        Le fichier Excel sera traité par lots de {BATCH_SIZE} élèves.
        Il doit contenir les colonnes suivantes : <br />
        <strong>Nom complet, Date de Naissance (formats acceptés : JJ/MM/AAAA, AAAA-MM-JJ, AAAA/MM/JJ, ou Excel Date Number), Sexe</strong> (obligatoires).<br />
        Ces trois champs combinés serviront à identifier les doublons pour la même école.
        <em>Lieu de Naissance, Adresse, Nom Complet du Tuteur, Téléphone du Tuteur, Nom du Père, Nom de la Mère, URL Photo</em> (optionnels).
      </p>

      <div className="flex items-center space-x-4 mb-6">
        <label htmlFor="excel-file" className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md cursor-pointer hover:bg-blue-600 transition">
          <UploadCloud size={20} className="mr-2" /> Choisir un fichier
        </label>
        <input
          type="file"
          id="excel-file"
          accept=".xlsx, .xls"
          onChange={handleFileChange}
          className="hidden"
        />
        {file && <span className="text-gray-700">{file.name}</span>}
      </div>

      <button
        onClick={handleImport}
        className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition"
        disabled={isLoading || !file}
      >
        {isLoading ? (
          <>
            <Loader2 className="animate-spin inline-block mr-2" size={20} />
            Importation en cours... ({overallProgress}% {totalBatches > 0 ? ` - Lot ${currentBatch}/${totalBatches}` : ''})
          </>
        ) : (
          <>
            <CheckCircle className="inline-block mr-2" size={20} />
            Lancer Importation
          </>
        )}
      </button>

      {importSummary && (
        <div className="mt-6 p-4 border rounded-md bg-gray-50">
          <h3 className="text-lg font-semibold mb-2">Résumé de l'Importation :</h3>
          <p className="text-gray-700">✔ Élèves ajoutés : <span className="font-bold text-green-600">{importSummary.studentsAdded}</span></p>
          <p className="text-gray-700">✖ Échecs : <span className="font-bold text-red-600">{importSummary.studentsFailed}</span></p>
          <p className="text-gray-700">⚠ Doublons ignorés : <span className="font-bold text-orange-600">{importSummary.studentsDuplicates}</span></p>

          {importSummary.failedStudents.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-600 mb-2">Détails des erreurs :</h4>
              <ul className="list-disc list-inside text-sm text-gray-700">
                {importSummary.failedStudents.map((fail, idx) => (
                  <li key={idx}>
                    Élève: {fail.data["Nom complet"] || 'N/A'} – Erreur : {fail.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {importSummary.duplicateStudents.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-orange-600 mb-2">Détails des doublons ignorés :</h4>
              <ul className="list-disc list-inside text-sm text-gray-700">
                {importSummary.duplicateStudents.map((dup, idx) => (
                  <li key={idx}>
                    Élève: {dup.data["Nom complet"] || 'N/A'} – Raison : {dup.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => {
          if (typeof onReturnToEleves === 'function') {
            onReturnToEleves();
          }
        }}
        className="mt-4 w-full bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition"
      >
        ← Retour à la gestion des élèves
      </button>
    </div>
  );
};

export default StudentExcelImport;
